import { NiceChannelBox } from '@/features/analytics/components/nice-channel-box'
import { AdminsContacts } from '@/features/analytics/invite-links/ui/Marking/components/AdminsContacts'
import { AnalyticsTableRowType } from '@/features/analytics/types'
import { QuestionSVGIcon } from '@/shared/assets'
import { useCopyToClipboard } from '@/shared/hooks'
import { Input, RubleIconInline, Toast } from '@/shared/ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import clsx from 'clsx'
import React from 'react'
import {
    parseTelegramPeerReference,
    telegramGetPeerTmeUrl,
} from '@synapex/shared2/telegram'
import { linkMethods } from '@synapex/shared2/entities'

type PlatformProps = {
    groupId: number
    analyticsTableRowType: AnalyticsTableRowType
    handleSelectedAdminContactIdChange: (
        editingRowId: number,
        selectedAdminContactId: number | null
    ) => void
}

const Platform: React.FC<PlatformProps> = ({
    groupId,
    analyticsTableRowType,
    handleSelectedAdminContactIdChange,
}) => {
    const { copy, toastVisible, toastMessage, hideToast } = useCopyToClipboard()

    const channelRef = parseTelegramPeerReference(analyticsTableRowType.name)
    const channelLink = channelRef
        ? telegramGetPeerTmeUrl(channelRef)?.href
        : undefined

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (channelLink) {
            copy(channelLink)
        }
    }

    const hasRnkStatus = analyticsTableRowType.rknStatus === 'active'

    return (
        <div className="flex flex-col gap-[6px]">
            <div className="flex gap-9">
                <div className="flex items-end gap-[6px]">
                    <h2 className="text-sm font-semibold">Площадка</h2>

                    <Tooltip>
                        <TooltipTrigger>
                            <QuestionSVGIcon className="cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                            <p>Информация о площадке</p>
                        </TooltipContent>
                    </Tooltip>
                </div>

                <div className="flex gap-2">
                    <div className="flex w-20 flex-col">
                        <span className="text-[10px] text-synapex-light-gray">
                            Стоимость
                        </span>
                        <span className="overflow-hidden whitespace-nowrap text-xs font-medium">
                            {new Intl.NumberFormat('ru-RU').format(
                                analyticsTableRowType.budget ?? 0
                            )}{' '}
                            <RubleIconInline />{' '}
                        </span>
                    </div>

                    <div className="flex w-20 flex-col">
                        <span className="text-[10px] text-synapex-light-gray">
                            Кол-во ПДП
                        </span>
                        <span className="overflow-hidden whitespace-nowrap text-xs font-medium">
                            {new Intl.NumberFormat('ru-RU').format(
                                analyticsTableRowType.publisher
                                    ?.telegram_channel_subscribers ?? 0
                            )}
                        </span>
                    </div>

                    <div className="flex w-20 flex-col">
                        <span className="text-[10px] text-synapex-light-gray">
                            Средний охват
                        </span>
                        <span className="overflow-hidden whitespace-nowrap text-xs font-medium">
                            {new Intl.NumberFormat('ru-RU').format(
                                analyticsTableRowType.publisher
                                    ?.average_reach ?? 0
                            )}
                        </span>
                    </div>

                    <div className="flex w-20 flex-col">
                        <span className="text-[10px] text-synapex-light-gray">
                            Прогноз СРМ
                        </span>
                        <span className="overflow-hidden whitespace-nowrap text-xs font-medium">
                            {(
                                analyticsTableRowType.CPMForcasted * 1000
                            ).toFixed(0)}{' '}
                            <RubleIconInline />{' '}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-start gap-8">
                <div className="flex w-[448px] items-center gap-2">
                    <div className="relative flex w-[146px] flex-col gap-[6px]">
                        <span className="text-[10px] text-synapex-light-gray">
                            Название канала
                        </span>

                        <NiceChannelBox
                            {...channelRef}
                            title={analyticsTableRowType.title}
                            className={clsx('name-cell__channel-box', {
                                new:
                                    new Date().getTime() -
                                        new Date(
                                            analyticsTableRowType.creationDate
                                        ).getTime() <=
                                    1 * 10 * 1000,
                            })}
                            rknStatus={analyticsTableRowType.rknStatus}
                            photoUrl={analyticsTableRowType.photoUrl}
                            toastClassName="toast__marking-modal"
                        />

                        {hasRnkStatus && (
                            <span className="absolute -bottom-[20px] text-[10px] text-synapex-light-gray">
                                Есть в реестре РКН
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col gap-[6px]">
                        <span className="text-[10px] text-synapex-light-gray">
                            Контакт админа
                        </span>
                        <AdminsContacts
                            {...linkMethods.getCamelFromLd(
                                analyticsTableRowType
                            )}
                            groupId={groupId}
                            publisher={analyticsTableRowType.publisher}
                            handleSelectedAdminContactIdChange={
                                handleSelectedAdminContactIdChange
                            }
                        />
                    </div>

                    <div className="flex flex-col gap-[6px]">
                        <span className="text-[10px] text-synapex-light-gray">
                            Ссылка на канал
                        </span>
                        <Input
                            onClick={handleCopy}
                            innerClassName="h-[32px] text-blue-primary cursor-pointer"
                            value={channelLink}
                            readOnly
                            shouldShowClearButton={false}
                        />
                        <Toast
                            message={toastMessage}
                            visible={toastVisible}
                            onClose={hideToast}
                            greenSwooshIcon={true}
                            className="toast__marking-modal"
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Platform
